import React from 'react';
import { Loader2, Save, Check, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SharedRigGallery from '@/components/admin/SharedRigGallery';
import { inputCls, labelCls, slugify } from './rigConstants';

/**
 * Booking Details tab of the RigDetailEditor — owns no state, just renders the
 * booking rig form from the props the parent editor passes down.
 */
export default function RigBookingTab({
  isNew,
  keyVal, onKey,
  name, onName,
  tag, onTag,
  description, onDescription,
  specs, onSpecs,
  galleryImages, onGalleryImages,
  sortOrder, onSortOrder,
  active, onToggleActive,
  canDelete, deleting, onDelete,
  saving, saved, onSave,
}) {
  return (
    <div className="space-y-4">
      {isNew && (
        <div>
          <Label className={labelCls}>KEY (unique, e.g. custom_rig)</Label>
          <Input value={keyVal} onChange={(e) => onKey(e.target.value)} placeholder="my_new_rig" className={inputCls} />
          <p className="text-white/20 text-[10px] mt-1.5">Also becomes the showcase URL slug: /simulators/{slugify(keyVal) || '...'}</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <Label className={labelCls}>NAME</Label>
          <Input value={name} onChange={(e) => onName(e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>TAG</Label>
          <Input value={tag} onChange={(e) => onTag(e.target.value)} className={inputCls} />
        </div>
      </div>
      <div>
        <Label className={labelCls}>DESCRIPTION</Label>
        <Textarea value={description} onChange={(e) => onDescription(e.target.value)} rows={4} className="bg-[#111115] border-white/10 text-white placeholder:text-white/20 focus:border-primary/60" />
      </div>
      <div>
        <Label className={labelCls}>FULL BUILD SPECS (one per line)</Label>
        <Textarea value={specs} onChange={(e) => onSpecs(e.target.value)} rows={6} placeholder={'e.g.\nWheel base: Simucube 2 Pro (25 Nm)\nPedals: Heusinkveld Sprint\nPC: RTX 4080 / Ryzen 7 7800X3D'} className="bg-[#111115] border-white/10 text-white placeholder:text-white/30 focus:border-primary/60" />
        <p className="text-white/30 text-[10px] mt-1.5">Shown in a popup when a customer clicks 'View full specs' on this rig card.</p>
      </div>
      <div>
        <Label className={labelCls}>RIG PHOTOS (shared with showcase page)</Label>
        <p className="text-white/30 text-[10px] mb-2">One library for both pages. Tick each photo to choose where it appears. The first "Booking" photo is the rig card image.</p>
        <SharedRigGallery photos={galleryImages} onChange={onGalleryImages} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className={labelCls}>SORT ORDER</Label>
          <Input type="number" value={sortOrder} onChange={(e) => onSortOrder(e.target.value)} className={inputCls} />
        </div>
        <div className="flex items-end">
          <button type="button" onClick={onToggleActive}
            className={`text-xs font-bold tracking-wider px-3 py-2.5 border transition-colors ${active ? 'border-primary/30 text-primary hover:bg-primary/5' : 'border-white/10 text-white/40 hover:text-white'}`}>
            {active ? 'ACTIVE' : 'HIDDEN'}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/5 gap-2">
        <div className="flex items-center gap-2">
          {canDelete && (
            <button type="button" onClick={onDelete} disabled={deleting}
              className="flex items-center gap-1.5 text-xs font-bold tracking-wider px-3 py-2.5 border border-red-500/20 text-red-400 hover:bg-red-500/5 transition-colors disabled:opacity-40">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} DELETE RIG
            </button>
          )}
        </div>
        <button onClick={onSave} disabled={saving || (isNew && !keyVal.trim())}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-2.5 transition-all disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'SAVING' : saved ? 'SAVED' : 'SAVE RIG'}
        </button>
      </div>
    </div>
  );
}